import { useEffect, useState } from "react";
import type { Store } from "@ai-pos/shared";
import { storesApi } from "../api/resources.js";

interface FormState {
  id: string | null;
  name: string;
  address: string;
  phone: string;
  registrationNumber: string;
  isActive: boolean;
}

function emptyForm(): FormState {
  return { id: null, name: "", address: "", phone: "", registrationNumber: "", isActive: true };
}

export function StoreAdmin() {
  const [stores, setStores] = useState<Store[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    storesApi
      .list()
      .then((res) => setStores(res.stores))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  };

  useEffect(load, []);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        address: form.address,
        phone: form.phone,
        registrationNumber: form.registrationNumber,
        isActive: form.isActive,
      };
      if (form.id) {
        await storesApi.update(form.id, payload);
      } else {
        await storesApi.create(payload);
      }
      setForm(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack">
      <div className="row">
        <h2 style={{ flex: 1 }}>店舗一覧</h2>
        <button className="btn btn-primary" onClick={() => setForm(emptyForm())}>
          新規店舗
        </button>
      </div>
      <p style={{ color: "var(--color-text-muted)" }}>
        新規店舗を追加すると、作成した管理者に自動的にアクセス権が付与されます。他のスタッフに追加したい場合は「スタッフ」画面から割り当ててください。
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>店舗名</th>
              <th>住所</th>
              <th>電話番号</th>
              <th>状態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {stores.map((store) => (
              <tr key={store.id}>
                <td>{store.name}</td>
                <td>{store.address}</td>
                <td>{store.phone}</td>
                <td>
                  {store.isActive ? <span className="badge">営業中</span> : <span className="badge badge-danger">停止中</span>}
                </td>
                <td>
                  <button
                    className="btn"
                    onClick={() =>
                      setForm({
                        id: store.id,
                        name: store.name,
                        address: store.address,
                        phone: store.phone,
                        registrationNumber: store.registrationNumber,
                        isActive: store.isActive,
                      })
                    }
                  >
                    編集
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="modal-backdrop" onClick={() => setForm(null)}>
          <div className="modal stack" onClick={(e) => e.stopPropagation()}>
            <h3>{form.id ? "店舗編集" : "新規店舗"}</h3>
            <div className="form-field">
              <label htmlFor="store-admin-name">店舗名</label>
              <input id="store-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-field">
              <label htmlFor="store-admin-address">住所</label>
              <input
                id="store-admin-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="form-field">
              <label htmlFor="store-admin-phone">電話番号</label>
              <input id="store-admin-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="form-field">
              <label htmlFor="store-admin-registration">登録番号（インボイス制度・任意）</label>
              <input
                id="store-admin-registration"
                value={form.registrationNumber}
                onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
              />
            </div>
            {form.id && (
              <label className="row" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  style={{ minHeight: "auto", width: 20, height: 20 }}
                />
                営業中
              </label>
            )}
            <div className="row">
              <button className="btn" onClick={() => setForm(null)}>
                キャンセル
              </button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !form.name}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
